import {
  AfterViewInit,
  ChangeDetectorRef,
  Component,
  OnDestroy,
  OnInit,
} from '@angular/core';
import { FormControl, UntypedFormControl } from '@angular/forms';
import {
  Asset,
  DataService,
  FormInputComponent,
  NotificationService,
  SharedModule,
  StringCustomFieldConfig,
} from '@vendure/admin-ui/core';
import { Subscription } from 'rxjs';

import { H1Source, H1StyleMode, PageType, TextValue, WebPresenceConfigData } from '../ui-types';
import { safeJSONParse } from '../utils';
import { TextValueInputComponent } from './text-value-input.component';

@Component({
  template: `
    <!-- Use Built-in SEO (only for product/collection...) -->
    <div *ngIf="showUseBuiltInOption">
      <vdr-form-field [label]="'Use Built-in SEO Settings'">
        <input type="checkbox" [formControl]="useBuiltInSeoControl" />
        <span class="ml-2">
          Use built-in SEO settings
          ({{
            (pageType === 'product' || pageType === 'product-specific')
              ? 'product name, description, featured asset'
              : 'collection name, description, featured asset'
          }})
        </span>
      </vdr-form-field>
    </div>

    <!-- SEO Settings -->
    <vdr-card [title]="'SEO Settings'" class="mt-2 mb-2" *ngIf="!isUsingBuiltInSeo">
      <!-- Hide SEO Title/Desc if using built-in -->
      <vdr-form-field [label]="'Title Tag'" class="mb-2">
        <input type="text" [formControl]="seoTitleControl" />
      </vdr-form-field>
      <vdr-form-field [label]="'Meta Description'">
        <input type="text" [formControl]="seoDescriptionControl" />
      </vdr-form-field>
    </vdr-card>

      <!-- Open Graph Settings (hidden when using built-in SEO) -->
      <vdr-card [title]="'Open Graph Settings'" *ngIf="!isUsingBuiltInSeo" class="mb-2">
        <!-- OG Title -->
        <vdr-form-field [label]="'Open Graph Title'" class="mb-2">
          <input type="checkbox" [formControl]="ogTitleSameAsSeoControl" />
          <span class="ml-2">Same as SEO Title Tag</span>
        </vdr-form-field>
        <!-- Hide OG title input when same-as is true -->
        <vdr-form-field *ngIf="!ogTitleSameAsSeoControl.value" class="mb-2">
          <input type="text" [formControl]="ogTitleControl" />
        </vdr-form-field>

        <!-- OG Description -->
        <vdr-form-field [label]="'Open Graph Description'" class="mb-2">
          <input type="checkbox" [formControl]="ogDescriptionSameAsSeoControl" />
          <span class="ml-2">Same as SEO Meta Description</span>
        </vdr-form-field>
        <!-- Hide OG description input when same-as is true -->
        <vdr-form-field *ngIf="!ogDescriptionSameAsSeoControl.value" class="mb-2">
          <input type="text" [formControl]="ogDescriptionControl" />
        </vdr-form-field>



        <!-- OG Image (stored in metaAssetId to avoid migrations) -->
        <vdr-form-field [label]="'Open Graph Image'">
          <vdr-dynamic-form-input
            [control]="ogImageControl"
            [def]="{
              type: 'relation',
              entity: 'Asset',
              ui: { component: 'relation-form-input' }
            }"
          ></vdr-dynamic-form-input>
        </vdr-form-field>
      </vdr-card>
    <!-- Sitemap indexing is still relevant, keep visible -->
    <vdr-form-field label="Allow Sitemap Indexing">
        <clr-toggle-wrapper>
            <input type="checkbox" clrToggle [formControl]="sitemapIndexingControl" />
        </clr-toggle-wrapper>
    </vdr-form-field>

    <!-- H1 Heading Settings -->
    <vdr-card [title]="'H1 Heading (SEO)'" class="mt-2">
      <vdr-form-field [label]="'H1 Source'" class="mb-2">
        <select [formControl]="h1SourceControl">
          <option value="auto">Auto - first visible section title becomes H1</option>
          <option value="custom">Custom - display a custom H1 above sections</option>
          <option value="none">None - no H1 injected</option>
        </select>
      </vdr-form-field>
      <div *ngIf="h1SourceControl.value === 'custom'" class="mb-2">
        <label class="clr-control-label mb-1">H1 Text</label>
        <text-value-input
          [formControl]="h1TextControl"
          [readonly]="readonly"
          [config]="h1TextConfig">
        </text-value-input>
      </div>
      <vdr-form-field *ngIf="h1SourceControl.value === 'custom' && h1TextFormat === 'plain'" [label]="'H1 Style Mode'" class="mb-2">
        <select [formControl]="h1StyleModeControl">
          <option value="auto">Auto (same as title style)</option>
          <option value="custom">Custom styles</option>
          <option value="sr-only">Screen-reader only (invisible)</option>
        </select>
      </vdr-form-field>
      <div *ngIf="h1SourceControl.value === 'custom' && h1TextFormat === 'plain' && h1StyleModeControl.value === 'custom'" class="mb-2">
        <div class="clr-row mb-2">
          <vdr-form-field class="clr-col-6" [label]="'Font Size (px)'">
            <input type="number" [formControl]="h1FontSizeControl" min="1" />
          </vdr-form-field>
          <vdr-form-field class="clr-col-6" [label]="'Mobile Font Size (px)'">
            <input type="number" [formControl]="h1MobileFontSizeControl" min="1" />
          </vdr-form-field>
        </div>
        <div class="clr-row mb-2">
          <vdr-form-field class="clr-col-6" [label]="'Color'">
            <input type="color" [formControl]="h1ColorControl" />
          </vdr-form-field>
          <vdr-form-field class="clr-col-6" [label]="'Alignment'">
            <select [formControl]="h1AlignmentControl">
              <option value="left">Left</option>
              <option value="center">Center</option>
              <option value="right">Right</option>
            </select>
          </vdr-form-field>
        </div>
      </div>
    </vdr-card>
  `,
  standalone: true,
  imports: [SharedModule, TextValueInputComponent],
})
export class WebPresenceConfigComponent
  implements FormInputComponent<StringCustomFieldConfig>, OnInit, AfterViewInit, OnDestroy
{
  readonly: boolean;
  config: StringCustomFieldConfig;
  formControl: FormControl;

  pageType: PageType;

  webPresenceConfigData: WebPresenceConfigData;

  // Controls
  useBuiltInSeoControl = new FormControl();

  seoTitleControl = new FormControl();
  seoDescriptionControl = new FormControl();
  sitemapIndexingControl = new FormControl();

  ogTitleSameAsSeoControl = new FormControl();
  ogDescriptionSameAsSeoControl = new FormControl();
  ogTitleControl = new FormControl();
  ogDescriptionControl = new FormControl();
  ogImageControl = new UntypedFormControl();

  h1SourceControl = new FormControl();
  h1TextControl = new FormControl();
  h1StyleModeControl = new FormControl();
  h1FontSizeControl = new FormControl();
  h1MobileFontSizeControl = new FormControl();
  h1ColorControl = new FormControl();
  h1AlignmentControl = new FormControl();
  h1TextFormat: string = 'plain';
  h1TextConfig = { ui: { args: {} } } as StringCustomFieldConfig;

  private subs = new Subscription();
  private isInitializing = true;

  get showUseBuiltInOption(): boolean {
    return (
      this.pageType === PageType.Product ||
      this.pageType === PageType.Collection ||
      this.pageType === PageType.ProductSpecific ||
      this.pageType === PageType.CollectionSpecific
    );
  }

  get isUsingBuiltInSeo(): boolean {
    return this.showUseBuiltInOption && !!this.useBuiltInSeoControl.value;
  }

  constructor(
    private notificationService: NotificationService,
    private changeDetector: ChangeDetectorRef,
    private dataService: DataService,
  ) {}

  ngOnInit(): void {
    this.isInitializing = true;
    this.pageType = this.config.ui?.options?.pageType || PageType.General;

    // Flat defaults (no migration required; missing fields auto-filled)
    const defaults: WebPresenceConfigData = {
      useBuiltInSeo: false,
      title: '',
      description: '',
      metaAssetId: null,

      openGraphTitleSameAsSeoTitle: true,
      openGraphDescriptionSameAsSeoDescription: true,
      openGraphTitle: '',
      openGraphDescription: '',

      sitemapIndexing: true,

      h1Source: 'auto' as H1Source,
      h1Text: { format: 'plain', i18n: {} } as TextValue,
      h1StyleMode: 'auto' as H1StyleMode,
      h1FontSize: 32,
      h1MobileFontSize: 16,
      h1Color: '',
      h1Alignment: 'left',
    };

    const parsedValue = this.formControl.value;
    //TODO: remove after migrating/republish all old data
    const parsed =
      typeof parsedValue === 'string'
        ? safeJSONParse<Partial<WebPresenceConfigData>>(
            parsedValue,
            this.notificationService,
            defaults,
          ) || {}
        : (parsedValue as Partial<WebPresenceConfigData> | null) || {};

    // Migrate old h1Text plain string to TextValue
    let h1Text = parsed.h1Text ?? defaults.h1Text;
    if (typeof h1Text === 'string') {
      h1Text = { format: 'plain', i18n: h1Text ? { en: h1Text } : {} } as TextValue;
    }

    this.webPresenceConfigData = {
      ...defaults,
      ...parsed,
      h1Text,
      metaAssetId: parsed.metaAssetId ?? defaults.metaAssetId,
      sitemapIndexing: parsed.sitemapIndexing ?? defaults.sitemapIndexing,
      openGraphTitleSameAsSeoTitle:
        parsed.openGraphTitleSameAsSeoTitle ?? defaults.openGraphTitleSameAsSeoTitle,
      openGraphDescriptionSameAsSeoDescription:
        parsed.openGraphDescriptionSameAsSeoDescription ??
        defaults.openGraphDescriptionSameAsSeoDescription,
    };

    this.initializeFormControls();
    this.applyReadonlyState();

    // Listen pageType changes (your existing pattern)
    const cfgChanges$ = this.config.ui?.changes;
    if (cfgChanges$) {
      this.subs.add(
        cfgChanges$.subscribe((data:any) => {
          if (data && data !== this.pageType) {
            this.pageType = data;
            this.handlePageTypeChange();
            this.changeDetector.detectChanges();
          }
        }),
      );
    }

    // Load OG image asset (stored in metaAssetId)
    if (this.webPresenceConfigData.metaAssetId) {
      this.subs.add(
        this.dataService.product
          .getAsset(this.webPresenceConfigData.metaAssetId.toString())
          .mapSingle((res) => res.asset)
          .subscribe((asset) => this.ogImageControl.setValue(asset)),
      );
    } else {
      this.ogImageControl.setValue(null, { emitEvent: false });
    }
  }

  ngAfterViewInit(): void {
    // Use built-in
    this.subs.add(this.useBuiltInSeoControl.valueChanges.subscribe(this.onUseBuiltInSeoChange));

    // SEO
    this.subs.add(this.seoTitleControl.valueChanges.subscribe(this.onSeoTitleChange));
    this.subs.add(this.seoDescriptionControl.valueChanges.subscribe(this.onSeoDescriptionChange));
    this.subs.add(this.sitemapIndexingControl.valueChanges.subscribe(this.onSitemapIndexingChange));

    // OG
    this.subs.add(this.ogTitleSameAsSeoControl.valueChanges.subscribe(this.onOgTitleSameAsSeoChange));
    this.subs.add(
      this.ogDescriptionSameAsSeoControl.valueChanges.subscribe(this.onOgDescriptionSameAsSeoChange),
    );
    this.subs.add(this.ogTitleControl.valueChanges.subscribe(this.onOgTitleChange));
    this.subs.add(this.ogDescriptionControl.valueChanges.subscribe(this.onOgDescriptionChange));
    this.subs.add(this.ogImageControl.valueChanges.subscribe(this.onOgImageChange));

    this.subs.add(this.h1SourceControl.valueChanges.subscribe(this.onH1SourceChange));
    this.subs.add(this.h1TextControl.valueChanges.subscribe(this.onH1TextChange));
    this.subs.add(this.h1StyleModeControl.valueChanges.subscribe(this.onH1StyleModeChange));
    this.subs.add(this.h1FontSizeControl.valueChanges.subscribe(this.onH1FontSizeChange));
    this.subs.add(this.h1MobileFontSizeControl.valueChanges.subscribe(this.onH1MobileFontSizeChange));
    this.subs.add(this.h1ColorControl.valueChanges.subscribe(this.onH1ColorChange));
    this.subs.add(this.h1AlignmentControl.valueChanges.subscribe(this.onH1AlignmentChange));
    setTimeout(() => {
      this.isInitializing = false;
    });
  }

  ngOnDestroy(): void {
    this.subs.unsubscribe();
  }

  private initializeFormControls(): void {
    const d = this.webPresenceConfigData;

    this.useBuiltInSeoControl.setValue(!!d.useBuiltInSeo, { emitEvent: false });

    this.seoTitleControl.setValue(d.title ?? '', { emitEvent: false });
    this.seoDescriptionControl.setValue(d.description ?? '', { emitEvent: false });
    this.sitemapIndexingControl.setValue(d.sitemapIndexing !== false, { emitEvent: false });

    this.ogTitleSameAsSeoControl.setValue(d.openGraphTitleSameAsSeoTitle !== false, {
      emitEvent: false,
    });
    this.ogDescriptionSameAsSeoControl.setValue(d.openGraphDescriptionSameAsSeoDescription !== false, {
      emitEvent: false,
    });

    this.ogTitleControl.setValue(d.openGraphTitle ?? '', { emitEvent: false });
    this.ogDescriptionControl.setValue(d.openGraphDescription ?? '', { emitEvent: false });

    this.h1SourceControl.setValue(d.h1Source ?? 'auto', { emitEvent: false });
    this.h1TextControl.setValue(JSON.stringify(d.h1Text ?? { format: 'plain', i18n: {} }), { emitEvent: false });
    this.h1TextFormat = d.h1Text?.format || 'plain';
    this.h1StyleModeControl.setValue(d.h1StyleMode ?? 'auto', { emitEvent: false });
    this.h1FontSizeControl.setValue(d.h1FontSize ?? 0, { emitEvent: false });
    this.h1MobileFontSizeControl.setValue(d.h1MobileFontSize ?? 0, { emitEvent: false });
    this.h1ColorControl.setValue(d.h1Color ?? '', { emitEvent: false });
    this.h1AlignmentControl.setValue(d.h1Alignment ?? 'left', { emitEvent: false });
  }

  private applyReadonlyState(): void {
    if (!this.readonly) {
      if (!this.showUseBuiltInOption) {
        this.useBuiltInSeoControl.disable({ emitEvent: false });
      } else {
        this.useBuiltInSeoControl.enable({ emitEvent: false });
      }
      return;
    }

    [
      this.useBuiltInSeoControl,
      this.seoTitleControl,
      this.seoDescriptionControl,
      this.sitemapIndexingControl,
      this.ogTitleSameAsSeoControl,
      this.ogDescriptionSameAsSeoControl,
      this.ogTitleControl,
      this.ogDescriptionControl,
      this.ogImageControl,
      this.h1SourceControl,
      this.h1TextControl,
      this.h1StyleModeControl,
      this.h1FontSizeControl,
      this.h1MobileFontSizeControl,
      this.h1ColorControl,
      this.h1AlignmentControl,
    ].forEach((c) => c.disable({ emitEvent: false }));
  }

  private onUseBuiltInSeoChange = (value: boolean) => {
    const v = !!value;
    if (this.webPresenceConfigData.useBuiltInSeo !== v) {
      this.webPresenceConfigData.useBuiltInSeo = v;
      this.updateJsonString();
    }
  };

  private onSeoTitleChange = (value: string) => {
    const v = value ?? '';
    if (this.webPresenceConfigData.title !== v) {
      this.webPresenceConfigData.title = v;
      this.updateJsonString();
    }
  };

  private onSeoDescriptionChange = (value: string) => {
    const v = value ?? '';
    if (this.webPresenceConfigData.description !== v) {
      this.webPresenceConfigData.description = v;
      this.updateJsonString();
    }
  };

  private onSitemapIndexingChange = (value: boolean) => {
    const v = value !== false;
    if (this.webPresenceConfigData.sitemapIndexing !== v) {
      this.webPresenceConfigData.sitemapIndexing = v;
      this.updateJsonString();
    }
  };

  private onOgTitleSameAsSeoChange = (value: boolean) => {
    const v = value !== false;
    if (this.webPresenceConfigData.openGraphTitleSameAsSeoTitle !== v) {
      this.webPresenceConfigData.openGraphTitleSameAsSeoTitle = v;
      this.updateJsonString();
    }
  };

  private onOgDescriptionSameAsSeoChange = (value: boolean) => {
    const v = value !== false;
    if (this.webPresenceConfigData.openGraphDescriptionSameAsSeoDescription !== v) {
      this.webPresenceConfigData.openGraphDescriptionSameAsSeoDescription = v;
      this.updateJsonString();
    }
  };

  private onOgTitleChange = (value: string) => {
    const v = value ?? '';
    if (this.webPresenceConfigData.openGraphTitle !== v) {
      this.webPresenceConfigData.openGraphTitle = v;
      this.updateJsonString();
    }
  };

  private onOgDescriptionChange = (value: string) => {
    const v = value ?? '';
    if (this.webPresenceConfigData.openGraphDescription !== v) {
      this.webPresenceConfigData.openGraphDescription = v;
      this.updateJsonString();
    }
  };

  private onOgImageChange = (value: Asset | null) => {
    const id = value ? value.id : null;
    if (this.webPresenceConfigData.metaAssetId !== id) {
      this.webPresenceConfigData.metaAssetId = id;
      this.updateJsonString();
    }
  };

  private onH1SourceChange = (value: string) => {
    const v = (value || 'auto') as H1Source;
    if (this.webPresenceConfigData.h1Source !== v) {
      this.webPresenceConfigData.h1Source = v;
      this.updateJsonString();
    }
  };

  private onH1TextChange = (value: string) => {
    const parsed = safeJSONParse<TextValue>(value ?? '', this.notificationService, { format: 'plain', i18n: {} });
    if (parsed) {
      this.webPresenceConfigData.h1Text = parsed;
      this.h1TextFormat = parsed.format || 'plain';
      this.updateJsonString();
    }
  };

  private onH1StyleModeChange = (value: string) => {
    const v = (value || 'auto') as H1StyleMode;
    if (this.webPresenceConfigData.h1StyleMode !== v) {
      this.webPresenceConfigData.h1StyleMode = v;
      this.updateJsonString();
    }
  };

  private onH1FontSizeChange = (value: number) => {
    const v = value || 0;
    if (this.webPresenceConfigData.h1FontSize !== v) {
      this.webPresenceConfigData.h1FontSize = v;
      this.updateJsonString();
    }
  };

  private onH1MobileFontSizeChange = (value: number) => {
    const v = value || 0;
    if (this.webPresenceConfigData.h1MobileFontSize !== v) {
      this.webPresenceConfigData.h1MobileFontSize = v;
      this.updateJsonString();
    }
  };

  private onH1ColorChange = (value: string) => {
    const v = value ?? '';
    if (this.webPresenceConfigData.h1Color !== v) {
      this.webPresenceConfigData.h1Color = v;
      this.updateJsonString();
    }
  };

  private onH1AlignmentChange = (value: string) => {
    const v = value || 'left';
    if (this.webPresenceConfigData.h1Alignment !== v) {
      this.webPresenceConfigData.h1Alignment = v;
      this.updateJsonString();
    }
  };

  private handlePageTypeChange(): void {
    if (!this.showUseBuiltInOption && this.useBuiltInSeoControl.value) {
      this.useBuiltInSeoControl.setValue(false, { emitEvent: false });
      this.webPresenceConfigData.useBuiltInSeo = false;
      this.updateJsonString();
    }
    this.applyReadonlyState();
  }

  private updateJsonString(): void {
    this.formControl.setValue(this.webPresenceConfigData as any, { emitEvent: !this.isInitializing });
    if (!this.isInitializing) {
      this.formControl.markAsDirty();
    }
  }
}
